import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Switch,
  FormControlLabel,
  Box,
  Stack,
  Typography,
  Divider,
  Alert,
  Button,
  Chip,
  Tooltip,
  Paper,
  Grid,
} from "@mui/material";
import {
  Add as AddIcon,
  Layers as LayersIcon,
  Block as BlockIcon,
  Gavel as GavelIcon,
} from "@mui/icons-material";
import InfoIcon from "@/assets/svgs/info.svg";
import { format } from "date-fns";
import { ModalV2, TextInput, DatePicker } from "@/components";
import { 
  DutyColumn, 
  DutyColumnInput,
  DutyColumnUpdateInput,
  DutyRule,
  DutyRuleInput,
  DutyColumnWithPaginatedRules,
  useCreateDutyColumnMutation,
  useUpdateDutyColumnMutation,
  useAddRuleToDutyColumnMutation,
  useUpdateRuleInDutyColumnMutation,
  useRemoveRuleFromDutyColumnMutation,
  RateType,
  ValueBasis,
  useGetDutyColumnQuery,
} from "@/services/product-catalogue/duty-columns-api";
import { calculateRuleDelta } from "@/utils/calculate-duty-rule-delta";
import { useSnackbar } from "@/hooks";
import DutyRuleFormSection from "../rule-form-section";
import { FormRule } from "../rule-form-section/helpers";

// Helpers to normalize date-only strings to explicit UTC boundaries
const isYmd = (s?: string) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
const toUtcStart = (ymd: string) => `${ymd}T00:00:00.000Z`;
const toUtcEnd = (ymd: string) => `${ymd}T23:59:59.999Z`;
const isoToYmd = (iso?: string) => {
  if (!iso) return undefined;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return undefined;
    return d.toISOString().slice(0, 10);
  } catch {
    return undefined;
  }
};

interface DutyColumnFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  column?: DutyColumn | null;
  existingColumns?: DutyColumn[];
}

interface FormState {
  name: string;
  description: string;
  citations: { url: string; postDate?: string }[];
  isActive: boolean;
  displayOrder: number;
  columnPriority: number;
  isStackable: boolean;
  mutuallyExclusiveGroup: string;
  rules: FormRule[];
  errors: Record<string, string>;
}

const DutyColumnForm: React.FC<DutyColumnFormProps> = ({
  open,
  onClose,
  onSuccess,
  column,
  existingColumns,
}) => {
  const isEditing = Boolean(column);
  const { show: showSnackbar } = useSnackbar();

  const getInitialState = (): FormState => {
    const maxDisplayOrder = existingColumns 
      ? Math.max(...existingColumns.map(col => col.displayOrder), 0) + 1 
      : 1;

    const getNextColumnPriority = () => {
      if (!existingColumns || existingColumns.length === 0) {
        return 10;
      }
      const existingPriorities = existingColumns
        .map(col => col.columnPriority || 10)
        .sort((a, b) => a - b);
      const maxPriority = Math.max(...existingPriorities);
      const nextPriority = maxPriority + 5;
      return Math.min(nextPriority, 100);
    };

    return {
      name: "",
      description: "",
      citations: [],
      isActive: true,
      displayOrder: maxDisplayOrder,
      columnPriority: getNextColumnPriority(),
      isStackable: true,
      mutuallyExclusiveGroup: "",
      rules: [],
      errors: {},
    };
  };

  const [formState, setFormState] = useState<FormState>(getInitialState);
  const originalFormState = useRef<FormState | null>(null);
  const rulesEndRef = useRef<HTMLDivElement>(null);

  const [createDutyColumn, { isLoading: isCreating }] = useCreateDutyColumnMutation();
  const [updateDutyColumn, { isLoading: isUpdating }] = useUpdateDutyColumnMutation();
  const [addRuleToDutyColumn, { isLoading: isAddingRule }] = useAddRuleToDutyColumnMutation();
  const [updateRuleInDutyColumn, { isLoading: isUpdatingRule }] = useUpdateRuleInDutyColumnMutation();
  const [removeRuleFromDutyColumn, { isLoading: isRemovingRule }] = useRemoveRuleFromDutyColumnMutation();

  const { data: columnData } = useGetDutyColumnQuery(
    {
      id: column?._id || '',
    },
    {
      skip: !column?._id,
    }
  );

  useEffect(() => {
    if (formState.rules.length > (originalFormState.current?.rules.length || 0)) {
      rulesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [formState.rules.length]);

  useEffect(() => {
    if (columnData?.data) {
      const columnDataTyped = columnData.data as DutyColumnWithPaginatedRules;
      const { rules, ...columnDetails } = columnDataTyped;
      const rulesArray = rules?.data || [];
      
      const loadedState: FormState = {
        name: columnDetails.name || '',
        description: columnDetails.description || '',
        citations: (columnDetails as any).citations?.map((c: any) => ({
          url: c?.url || '',
          // Normalize to UTC date-only to avoid local TZ shifts
          postDate: c?.postDate ? new Date(c.postDate).toISOString().slice(0, 10) : undefined
        })) || [],
        isActive: columnDetails.isActive ?? true,
        displayOrder: columnDetails.displayOrder ?? 1,
        columnPriority: columnDetails.columnPriority ?? 10,
        isStackable: columnDetails.isStackable ?? true,
        mutuallyExclusiveGroup: columnDetails.mutuallyExclusiveGroup || '',
        rules: Array.isArray(rulesArray) ? rulesArray.map((rule: DutyRule) => ({
          ...rule,
          rate: rule.rate ? (rule.rate * 100).toString() : '0',
          // Normalize rule dates to YYYY-MM-DD for the UI
          startDate: isoToYmd((rule as any).startDate) || '',
          endDate: isoToYmd((rule as any).endDate),
          showAdvanced: false,
          countries: rule.countries || [],
          countryExclusions: rule.countryExclusions || [],
          htsCodes: rule.htsCodes || [],
          exclusions: rule.exclusions || [],
          allowedStacksWith: rule.allowedStacksWith || [],
          nonMetalAllowedStacksWith: rule.nonMetalAllowedStacksWith || [],
          requiredWith: rule.requiredWith || [],
        })) : [],
        errors: {},
      };
      setFormState(loadedState);
      originalFormState.current = JSON.parse(JSON.stringify(loadedState));

    } else if (!column) {
      const initialState = getInitialState();
      setFormState(initialState);
      originalFormState.current = JSON.parse(JSON.stringify(initialState));
    }
  }, [column, columnData, existingColumns]);

  const updateFormField = <K extends keyof Omit<FormState, 'rules' | 'errors'>>(
    field: K,
    value: FormState[K]
  ) => {
    setFormState((prev: FormState) => ({ 
      ...prev, 
      [field]: value ?? '',
    }));
  };

  const addRule = () => {
    const newRule: FormRule = {
      priority: formState.rules.length + 1,
      countries: [],
      countryExclusions: [],
      htsCodes: [],
      exclusions: [],
      rate: "",
      startDate: format(new Date(), "yyyy-MM-dd"),
      endDate: format(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"),
      authority: undefined,
      rateType: RateType.ADVALOREM,
      valueBasis: ValueBasis.ENTERED_VALUE,
      isMetal: false,
      isExpanded: true,
    };
    setFormState((prev: FormState) => ({
      ...prev,
      rules: [...prev.rules, newRule]
    }));
  };

  const updateRule = useCallback((index: number, field: keyof FormRule, value: any) => {
    setFormState((prev: FormState) => {
      const updatedRules = [...prev.rules];
      updatedRules[index] = {
        ...updatedRules[index],
        [field]: value ?? '',
      };
      return {
        ...prev,
        rules: updatedRules,
      };
    });
  }, []);

  const duplicateRule = useCallback((index: number) => {
    const ruleToDuplicate = formState.rules[index];
    
    const newRule: FormRule = {
      ...JSON.parse(JSON.stringify(ruleToDuplicate)), // Deep copy
      _id: undefined,
      isExpanded: true,
    };

    const newRules = [...formState.rules, newRule];
    newRules.forEach((rule, i) => {
      rule.priority = i + 1;
    });

    setFormState(prev => ({
      ...prev,
      rules: newRules,
    }));
  }, [formState.rules]);

  const removeRule = useCallback((index: number) => {
    const updatedRules = formState.rules.filter((_: any, i: number) => i !== index);
    updatedRules.forEach((rule: FormRule, i: number) => {
      rule.priority = i + 1;
    });
    setFormState((prev: FormState) => ({
      ...prev,
      rules: updatedRules
    }));
  }, [formState.rules]);

  const toggleRuleExpanded = useCallback((index: number) => {
    setFormState((prev: FormState) => {
      const updatedRules = [...prev.rules];
      updatedRules[index] = {
        ...updatedRules[index],
        isExpanded: !updatedRules[index].isExpanded,
      };
      return {
        ...prev,
        rules: updatedRules,
      };
    });
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formState.name.trim()) {
      newErrors.name = "Column name is required";
    }
    // Validate citations (optional overall; but if provided, require valid URL)
    formState.citations.forEach((c, idx) => {
      const url = (c.url || '').trim();
      if (url.length > 0 && !/^https?:\/\//i.test(url)) {
        newErrors[`citation_${idx}_url`] = "Enter a valid http(s) URL or remove the citation";
      }
    });

    if (formState.columnPriority < 1 || formState.columnPriority > 100) {
      newErrors.columnPriority = "Priority must be between 1 and 100";
    }

    formState.rules.forEach((rule: FormRule, index: number) => {
      if (!rule.rate || isNaN(Number(rule.rate))) {
        newErrors[`rule_${index}_rate`] = "Rate must be a valid number";
      }

      if (!rule.startDate) {
        newErrors[`rule_${index}_startDate`] = "Start date is required";
      }

      if (rule.startDate && rule.endDate && new Date(rule.startDate) >= new Date(rule.endDate)) {
        newErrors[`rule_${index}_endDate`] = "End date must be after start date";
      }
    });

    setFormState((prev: FormState) => ({
      ...prev,
      errors: newErrors
    }));
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const areCitationsEqual = (a: { url: string; postDate?: string }[], b: { url: string; postDate?: string }[]) => {
      if (!Array.isArray(a) || !Array.isArray(b)) return false;
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        const ai = { url: (a[i].url || '').trim(), postDate: a[i].postDate || undefined };
        const bi = { url: (b[i].url || '').trim(), postDate: b[i].postDate || undefined };
        if (ai.url !== bi.url) return false;
        if ((ai.postDate || '') !== (bi.postDate || '')) return false;
      }
      return true;
    };

    try {
      if (isEditing && column && originalFormState.current) {
        const citationsChanged = !areCitationsEqual(formState.citations, originalFormState.current.citations || []);
        const columnDetailsChanged =
          formState.name !== originalFormState.current.name ||
          formState.description !== originalFormState.current.description ||
          citationsChanged ||
          formState.isActive !== originalFormState.current.isActive ||
          formState.displayOrder !== originalFormState.current.displayOrder ||
          formState.columnPriority !== originalFormState.current.columnPriority ||
          formState.isStackable !== originalFormState.current.isStackable ||
          formState.mutuallyExclusiveGroup !== originalFormState.current.mutuallyExclusiveGroup;

        if (columnDetailsChanged) {
          const columnUpdatePayload: Omit<DutyColumnUpdateInput, 'rules'> = {
          name: formState.name?.trim() || '',
          description: formState.description?.trim(),
          citations: formState.citations.filter(c => c.url?.trim()).map(c => ({
            url: c.url.trim(),
            // Send as midnight UTC to keep the selected date consistent in backend
            postDate: c.postDate ? `${c.postDate}T00:00:00.000Z` : undefined,
          })),
          isActive: formState.isActive,
          displayOrder: formState.displayOrder,
          columnPriority: formState.columnPriority,
          isStackable: formState.isStackable,
          mutuallyExclusiveGroup: formState.mutuallyExclusiveGroup?.trim(),
          };
          updateDutyColumn({
            id: column._id,
            dutyColumn: columnUpdatePayload,
          }).unwrap().catch(() => {
            showSnackbar('Failed to update column details.', '', 'error');
          });
        }

        const originalRules = originalFormState.current.rules;
        const updatedRules = formState.rules;

        const originalRuleMap = new Map(originalRules.map((r: FormRule) => [r._id, r]));
        const updatedRuleMap = new Map(updatedRules.filter((r: FormRule) => r._id).map((r: FormRule) => [r._id, r]));

        const rulesToAdd = updatedRules.filter((r: FormRule) => !r._id);
        const rulesToRemove = originalRules.filter((r: FormRule) => r._id && !updatedRuleMap.has(r._id));
        const rulesToUpdate: { ruleId: string, payload: any }[] = [];

        for (const updatedRule of updatedRules) {
          if (!updatedRule._id) continue;
          const originalRule = originalRuleMap.get(updatedRule._id);
          if (originalRule) {
            const originalRuleForCalc = {
              ...originalRule,
              rate: Number(originalRule.rate) / 100
            };
            const updatedRuleForCalc = {
              ...updatedRule,
              rate: Number(updatedRule.rate) / 100
            };

            const delta = calculateRuleDelta(originalRuleForCalc as DutyRule, updatedRuleForCalc as DutyRule);
            if (Object.keys(delta).length > 1) {
              rulesToUpdate.push({ ruleId: updatedRule._id, payload: delta });
            }
          }
        }
        
        for (const rule of rulesToAdd) {
          const { 
            isExpanded, 
            _id, 
            showAdvanced,
            ...restOfRule 
          } = rule;

          const ruleInput: DutyRuleInput = {
            ...restOfRule,
            rate: Number(rule.rate) / 100,
            // Anchor dates to UTC boundaries to avoid TZ drift
            startDate: isYmd(rule.startDate) ? toUtcStart(rule.startDate) : restOfRule.startDate,
            endDate: isYmd(rule.endDate || '') ? toUtcEnd(rule.endDate as string) : (rule.endDate || "2099-12-31T23:59:59.999Z"),
          };
          addRuleToDutyColumn({ columnId: column._id, rule: ruleInput }).unwrap().catch(() => {
            showSnackbar(`Failed to add new rule.`, '', 'error');
          });
        }

        for (const { ruleId, payload } of rulesToUpdate) {
          // Normalize any date-only fields in the update delta
          const normalizedPayload = { ...payload } as any;
          if (isYmd(normalizedPayload.startDate)) {
            normalizedPayload.startDate = toUtcStart(normalizedPayload.startDate);
          }
          if (isYmd(normalizedPayload.endDate)) {
            normalizedPayload.endDate = toUtcEnd(normalizedPayload.endDate);
          }
          updateRuleInDutyColumn({ columnId: column._id, ruleId, rule: normalizedPayload }).unwrap().catch(() => {
            showSnackbar(`Failed to update rule.`, '', 'error');
          });
        }

        for (const rule of rulesToRemove) {
          removeRuleFromDutyColumn({ columnId: column._id, ruleId: rule._id! }).unwrap().catch(() => {
            showSnackbar(`Failed to remove rule.`, '', 'error');
          });
        }

        showSnackbar('Duty column changes saved.', '', 'success');
        onSuccess();
        onClose();

      } else {
        const createData: DutyColumnInput = {
          name: formState.name?.trim() || '',
          description: formState.description?.trim(),
          citations: formState.citations.filter(c => c.url?.trim()).map(c => ({
            url: c.url.trim(),
            // Send as midnight UTC to keep the selected date consistent in backend
            postDate: c.postDate ? `${c.postDate}T00:00:00.000Z` : undefined,
          })),
          isActive: formState.isActive,
          displayOrder: formState.displayOrder,
          columnPriority: formState.columnPriority,
          isStackable: formState.isStackable,
          mutuallyExclusiveGroup: formState.mutuallyExclusiveGroup?.trim(),
          rules: formState.rules.map((rule: FormRule) => {
            const { isExpanded, _id, showAdvanced, ...restOfRule } = rule;
            return {
              ...restOfRule,
              rate: Number(rule.rate) / 100,
              // Anchor dates to UTC boundaries to avoid TZ drift
              startDate: isYmd(rule.startDate) ? toUtcStart(rule.startDate) : restOfRule.startDate,
              endDate: isYmd(rule.endDate || '') ? toUtcEnd(rule.endDate as string) : (rule.endDate || "2099-12-31T23:59:59.999Z"),
            };
          }),
        };
        createDutyColumn(createData).unwrap().then(() => {
          showSnackbar('Duty column created successfully!', '', 'success');
          onSuccess();
          onClose();
        }).catch(() => {
          showSnackbar('Failed to create duty column.', '', 'error');
        });
      }
      
    } catch (error) {
      console.error("Failed to prepare duty column save operation:", error);
      showSnackbar('An unexpected error occurred while preparing to save.', '', 'error');
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <ModalV2
      show={open}
      setShow={handleClose}
      title={isEditing ? 'Edit Duty Column' : 'Create Duty Column'}
      subTitle={isEditing ? 'Modify the duty column and its rules' : 'Set up a new duty column with tariff rules'}
      buttonText={isEditing ? 'Save Changes' : 'Create Column'}
      buttonAction={handleSubmit}
      isConfirmButtonLoading={isCreating || isUpdating || isAddingRule || isUpdatingRule || isRemovingRule}
      maxWidth="lg"
      modalStyles={{
        "& .MuiPaper-root": {
          maxWidth: "1100px",
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* Basic Information */}
          <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#1f2937', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <img src={InfoIcon} alt="Info" style={{ width: 24, height: 24 }} />
              Basic Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextInput
                  label="Column Name"
                  type="text"
                  value={formState.name}
                  handleChangeValue={(value) => updateFormField('name', value)}
                  error={!!formState.errors.name}
                  helperText={formState.errors.name}
                  placeholder="e.g., Section 301, China Tariff"
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formState.isActive}
                      onChange={(e) => updateFormField('isActive', e.target.checked)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Active</span>
                      {formState.isActive ? (
                        <Chip label="Enabled" size="small" color="success" />
                      ) : (
                        <Chip label="Disabled" size="small" />
                      )}
                    </Box>
                  }
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextInput
                  label="Description"
                  type="text"
                  value={formState.description}
                  handleChangeValue={(value) => updateFormField('description', value)}
                  placeholder="Brief description of the duty column"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Column Configuration */}
          <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#1f2937', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LayersIcon sx={{ color: '#15803d' }} />
              Column Configuration
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <TextInput
                  label="Display Order"
                  type="number"
                  value={formState.displayOrder.toString()}
                  handleChangeValue={(value) => updateFormField('displayOrder', Number(value))}
                  helperText="Order in UI (lower first)"
                  InputProps={{
                    inputProps: { min: 1 }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextInput
                  label="Column Priority"
                  type="number"
                  value={formState.columnPriority.toString()}
                  handleChangeValue={(value) => updateFormField('columnPriority', Number(value))}
                  error={!!formState.errors.columnPriority}
                  helperText={formState.errors.columnPriority || (isEditing ? "Calculation order (lower first)" : "Auto-calculated: highest existing + 5")}
                  InputProps={{
                    inputProps: { min: 1, max: 100 }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formState.isStackable}
                        onChange={(e) => updateFormField('isStackable', e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>Stackable</span>
                        <Tooltip title={formState.isStackable ? "Rates will add to other columns" : "Rate only applies if no other rate exists"}>
                          <img src={InfoIcon} alt="Info" style={{ width: 16, height: 16 }} />
                        </Tooltip>
                      </Box>
                    }
                  />
                  {formState.isStackable && (
                    <Chip 
                      icon={<LayersIcon />} 
                      label="Rates Stack" 
                      size="small" 
                      color="primary" 
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextInput
                  label="Exclusion Group"
                  type="text"
                  value={formState.mutuallyExclusiveGroup}
                  handleChangeValue={(value) => updateFormField('mutuallyExclusiveGroup', value)}
                  placeholder="e.g., steel-tariffs"
                  helperText="Columns in same group are mutually exclusive"
                  InputProps={{
                    startAdornment: formState.mutuallyExclusiveGroup && (
                      <BlockIcon fontSize="small" sx={{ mr: 1, color: '#ef4444' }} />
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight={500} gutterBottom>
                Priority Guide: MFN (5) → Section 301 (10) → Section 232 (20) → Other (30+)
              </Typography>
              {existingColumns && existingColumns.length > 0 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Existing Priorities:</strong> {
                    [...existingColumns]
                      .sort((a, b) => (a.columnPriority || 10) - (b.columnPriority || 10))
                      .map(col => `${col.name} (${col.columnPriority || 10})`)
                      .join(' → ')
                  }
                </Typography>
              )}
            </Alert>
          </Paper>

          {/* Citations Section */}
          <Paper elevation={0} sx={{ p: 3, backgroundColor: '#fff', border: '1px solid #e5e7eb' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" sx={{ color: '#1f2937', display: 'flex', alignItems: 'center', gap: 1 }}>
                FRN / CSMS Citations
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={() => {
                  setFormState(prev => ({
                    ...prev,
                    citations: [...prev.citations, { url: '', postDate: undefined }]
                  }));
                }}
                variant="outlined"
                size="small"
                sx={{ textTransform: 'none' }}
              >
                Add Citation
              </Button>
            </Stack>
            {formState.citations.length === 0 ? (
              <Alert severity="info">No citations added. These are optional.</Alert>
            ) : (
              <Stack spacing={2}>
                {formState.citations.map((c, idx) => (
                  <Paper key={idx} elevation={0} sx={{ p: 2, border: '1px dashed #d1d5db', borderRadius: 1 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={7}>
                        <TextInput
                          label={`Citation URL #${idx + 1}`}
                          type="text"
                          value={c.url}
                          handleChangeValue={(value) => {
                            setFormState(prev => {
                              const next = [...prev.citations];
                              next[idx] = { ...next[idx], url: value };
                              return { ...prev, citations: next };
                            });
                          }}
                          placeholder="https://www.federalregister.gov/... or https://www.cbp.gov/csms/..."
                          error={!!formState.errors[`citation_${idx}_url`]}
                          helperText={formState.errors[`citation_${idx}_url`]}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DatePicker
                          label="Post Date (optional)"
                          value={c.postDate || null}
                          onChangeValue={(d) => {
                            setFormState(prev => {
                              const next = [...prev.citations];
                              next[idx] = { ...next[idx], postDate: d || undefined };
                              return { ...prev, citations: next };
                            });
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <Button
                          color="error"
                          variant="text"
                          onClick={() => {
                            setFormState(prev => ({
                              ...prev,
                              citations: prev.citations.filter((_, i) => i !== idx)
                            }));
                          }}
                        >
                          Remove
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Stack>
            )}
          </Paper>

          <Divider />

          {/* Rules Section */}
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" sx={{ color: '#1f2937', display: 'flex', alignItems: 'center', gap: 1 }}>
                <GavelIcon sx={{ color: '#0B494B' }} />
                Duty Rules
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addRule}
                variant="contained"
                size="small"
                sx={{ 
                  textTransform: 'none',
                  backgroundColor: '#0B494B',
                  '&:hover': { backgroundColor: '#0a3a3c' }
                }}
              >
                Add Rule
              </Button>
            </Stack>

            {formState.rules.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No rules defined. Add rules to specify when this duty column applies.
              </Alert>
            )}

            <Stack spacing={2}>
              {formState.rules.map((rule: FormRule, index: number) => (
                <DutyRuleFormSection
                  key={rule._id || index} 
                  rule={rule}
                  index={index}
                  updateRule={updateRule}
                  removeRule={removeRule}
                  duplicateRule={duplicateRule}
                  toggleRuleExpanded={toggleRuleExpanded}
                  formErrors={formState.errors}
                          />
              ))}
            </Stack>

            <div ref={rulesEndRef} />
          </Box>
        </Stack>
      </Box>
    </ModalV2>
  );
};

export default DutyColumnForm; 